// src/components/ExcelUploadModal.jsx (또는 기존 경로)
import React, { useRef, useState } from 'react';
import { X, UploadCloud, FileSpreadsheet, Loader2, Download } from 'lucide-react';

export default function ExcelUploadModal({ 
  isOpen, 
  onClose, 
  onUpload, 
  isUploading,
  title = "엑셀 업로드", 
  description = "다운로드 받으신 양식에 맞게 작성된 엑셀(.xlsx, .csv) 파일을 업로드 해주세요.",
  onDownloadTemplate // 🌟 템플릿 다운로드 API 호출 함수 (Prop으로 받음)
}) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false); // 템플릿 다운로드 로딩 상태
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) setSelectedFile(file);
  };

  const handleSubmit = () => {
    if (selectedFile) {
      onUpload(selectedFile);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    onClose();
  };

  // 🌟 템플릿 다운로드 실행 핸들러
  const handleDownload = async () => {
    if (!onDownloadTemplate) return;
    setIsDownloading(true);
    try {
      await onDownloadTemplate();
    } catch (e) {
      alert("양식 다운로드에 실패했습니다.");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl animate-in zoom-in-95 duration-200">
        
        {/* 모달 헤더 */}
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-green-600" />
            {title}
          </h3>
          <button onClick={handleClose} disabled={isUploading} className="rounded-lg p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* 모달 바디 */}
        <div className="p-6">
          <p className="mb-4 text-sm text-slate-500 leading-relaxed">
            {description}
          </p>

          {/* 🌟 템플릿 다운로드 영역 (함수가 전달되었을 때만 렌더링) */}
          {onDownloadTemplate && (
            <div className="mb-5 flex items-center justify-between rounded-lg border border-blue-100 bg-blue-50/50 px-4 py-3">
              <span className="text-sm font-medium text-blue-800">입력 양식 파일이 필요하신가요?</span>
              <button 
                onClick={handleDownload}
                disabled={isDownloading || isUploading}
                className="flex items-center gap-1.5 rounded-md bg-white border border-blue-200 px-3 py-1.5 text-xs font-semibold text-blue-600 shadow-sm hover:bg-blue-50 transition-colors disabled:opacity-50"
              >
                {isDownloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                양식 다운로드
              </button>
            </div>
          )}

          {/* 드래그 앤 드롭 영역 */}
          <div 
            className={`flex justify-center rounded-lg border-2 border-dashed px-6 py-10 transition-colors ${selectedFile ? 'border-green-400 bg-green-50' : 'border-slate-300 hover:border-blue-400 hover:bg-slate-50'}`}
          >
            <div className="text-center">
              <UploadCloud className={`mx-auto h-12 w-12 ${selectedFile ? 'text-green-500' : 'text-slate-300'}`} />
              <div className="mt-4 flex text-sm leading-6 text-slate-600 justify-center">
                <label className="relative cursor-pointer rounded-md bg-transparent font-semibold text-blue-600 focus-within:outline-none hover:text-blue-500">
                  <span>{selectedFile ? '다른 파일 선택' : '파일 선택'}</span>
                  <input ref={fileInputRef} type="file" className="sr-only" accept=".xlsx, .xls, .csv" onChange={handleFileChange} disabled={isUploading} />
                </label>
                <p className="pl-1">{selectedFile ? '완료' : '또는 드래그 앤 드롭'}</p>
              </div>
              <p className="text-xs leading-5 text-slate-500 mt-1">
                {selectedFile ? <span className="font-bold text-slate-700">{selectedFile.name}</span> : 'CSV, XLSX up to 10MB'}
              </p>
            </div>
          </div>
        </div>

        {/* 모달 푸터 */}
        <div className="flex items-center justify-end gap-3 rounded-b-2xl bg-slate-50 px-6 py-4 border-t border-slate-100">
          <button onClick={handleClose} disabled={isUploading} className="h-10 rounded-lg border border-slate-300 bg-white px-5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
            취소
          </button>
          <button 
            onClick={handleSubmit} 
            disabled={!selectedFile || isUploading}
            className="flex items-center gap-2 h-10 rounded-lg bg-blue-600 px-5 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {isUploading && <Loader2 className="h-4 w-4 animate-spin" />}
            {isUploading ? '업로드 중...' : '데이터 등록'}
          </button>
        </div>

      </div>
    </div>
  );
}